/**
 * Reset Password Route (Story 1.8)
 *
 * Page for resetting password with a valid token.
 * - Validates token on mount
 * - Shows password form if token is valid
 * - Shows error message if token is invalid/expired
 * - Redirects to login on success
 */

import * as React from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
} from "@provenleap/ui";
import { PasswordInput } from "../../components/auth";
import { LoadingSpinner } from "../../components/icons";

// Search params type
type ResetPasswordSearch = {
  token?: string;
};

export const Route = createFileRoute("/_auth/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => {
    return {
      token: typeof search.token === "string" ? search.token : undefined,
    };
  },
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: "/_auth/reset-password" });

  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isValidating, setIsValidating] = React.useState(true);
  const [isValid, setIsValid] = React.useState(false);
  const [tokenError, setTokenError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

  // Validate token on mount
  React.useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setTokenError("No reset token provided. Please request a new password reset link.");
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`
        );
        const data = await response.json();

        if (data.data?.valid) {
          setIsValid(true);
        } else {
          setTokenError(
            data.data?.error === "Token expired"
              ? "This link has expired. Please request a new password reset link."
              : "This link has expired or already been used. Please request a new password reset link."
          );
        }
      } catch {
        setTokenError("Unable to verify reset link. Please try again.");
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, apiBaseUrl]);

  // Password validation
  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 8) errors.push("At least 8 characters");
    if (!/[A-Z]/.test(pwd)) errors.push("One uppercase letter");
    if (!/[a-z]/.test(pwd)) errors.push("One lowercase letter");
    if (!/[0-9]/.test(pwd)) errors.push("One number");
    return errors;
  };

  const passwordErrors = password ? validatePassword(password) : [];
  const passwordsMatch = confirmPassword && password === confirmPassword;
  const canSubmit = password && confirmPassword && passwordErrors.length === 0 && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      if (response.ok) {
        // Success - redirect to login with success message
        navigate({ to: "/login", search: { message: "password-reset-success" } });
      } else {
        const data = await response.json();

        if (data.type?.includes("invalid-reset-token")) {
          setTokenError("This link has expired or already been used. Please request a new password reset link.");
          setIsValid(false);
        } else if (data.type?.includes("validation-error")) {
          setError("Please check your password meets all requirements.");
        } else {
          setError("Unable to reset password. Please try again.");
        }
      }
    } catch {
      setError("Unable to connect. Please check your internet connection.");
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <Card className="!rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <LoadingSpinner className="h-8 w-8 text-primary" />
            <p className="mt-4 text-sm text-neutral-600">Verifying reset link...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Invalid token state
  if (!isValid || tokenError) {
    return (
      <Card className="!rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
        <CardHeader>
          <CardTitle>Unable to Reset Password</CardTitle>
          <CardDescription className="mt-2 text-neutral-700">
            {tokenError}
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex flex-col gap-3">
          <Button variant="brand" className="w-full cursor-pointer" asChild>
            <a href="/forgot-password">Request new reset link</a>
          </Button>
          <a href="/login" className="text-sm font-medium text-primary hover:underline flex items-center justify-center gap-1">
            <span>←</span> Back to login
          </a>
        </CardFooter>
      </Card>
    );
  }

  // Valid token - show password reset form
  return (
    <Card className="!rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <CardTitle>Create New Password</CardTitle>
        <CardDescription className="mt-2 text-neutral-700">
          Enter your new password below
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {error && (
          <div className="rounded-md bg-error/10 p-3 text-sm text-error mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="password" className="text-sm font-medium">
              New Password
            </label>
            <PasswordInput
              id="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
            />
            {/* Password requirements */}
            {password && passwordErrors.length > 0 && (
              <div className="mt-2 text-xs text-neutral-600">
                <p className="mb-1">Password must have:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {passwordErrors.map((err) => (
                    <li key={err} className="text-error">{err}</li>
                  ))}
                </ul>
              </div>
            )}
            {password && passwordErrors.length === 0 && (
              <p className="mt-2 text-xs text-green-600">✓ Password meets requirements</p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="confirmPassword" className="text-sm font-medium">
              Confirm Password
            </label>
            <PasswordInput
              id="confirmPassword"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="mt-1 text-xs text-error">Passwords do not match</p>
            )}
            {confirmPassword && passwordsMatch && (
              <p className="mt-1 text-xs text-green-600">✓ Passwords match</p>
            )}
          </div>

          <Button type="submit" variant="brand" className="w-full cursor-pointer" disabled={!canSubmit || isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Resetting password...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <a href="/login" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
          <span>←</span> Back to login
        </a>
      </CardFooter>
    </Card>
  );
}
